import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AdminShell } from "@/components/admin/admin-shell";
import { requireSchoolAdminOrOnboard, useSchoolContext } from "@/lib/school-context";
import { supabase } from "@/integrations/supabase/client";
import {
  Users, GraduationCap, UserCog, Wallet, Calendar as CalIcon,
  TrendingUp, Bell, ClipboardCheck, Plus,
} from "lucide-react";

export const Route = createFileRoute("/admin/")({
  ssr: false,
  beforeLoad: () => requireSchoolAdminOrOnboard(),
  head: () => ({ meta: [{ title: "School Admin — MAKAI" }] }),
  component: AdminDashboard,
});

interface Stats {
  learners: number;
  teachers: number;
  parents: number;
  classes: number;
  attendanceToday: { present: number; total: number };
  feesCollectedMtd: number;
  outstanding: number;
  currency: string;
}

function AdminDashboard() {
  const { active, loading } = useSchoolContext();
  const [stats, setStats] = useState<Stats | null>(null);
  const [events, setEvents] = useState<{ id: string; title: string; start_at: string; type: string }[]>([]);
  const [activity, setActivity] = useState<{ id: string; action: string; entity_type: string | null; created_at: string }[]>([]);
  const [notifs, setNotifs] = useState<{ id: string; title: string; body: string | null; created_at: string }[]>([]);

  useEffect(() => {
    if (!active) return;
    const sid = active.school_id;
    const today = new Date(); today.setHours(0,0,0,0);
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0,10);
    const todayStr = today.toISOString().slice(0,10);

    (async () => {
      const [learners, teachers, parents, classes, attToday, payMonth, invOpen, ev, audit, n] = await Promise.all([
        supabase.from("learners").select("id", { head: true, count: "exact" }).eq("school_id", sid).eq("status", "active"),
        supabase.from("staff").select("id", { head: true, count: "exact" }).eq("school_id", sid).eq("role_type", "teacher").eq("status", "active"),
        supabase.from("parents").select("id", { head: true, count: "exact" }).eq("school_id", sid),
        supabase.from("streams").select("id", { head: true, count: "exact" }).eq("school_id", sid),
        supabase.from("attendance").select("status").eq("school_id", sid).eq("date", todayStr),
        supabase.from("payments").select("amount").eq("school_id", sid).gte("payment_date", monthStart),
        supabase.from("invoices").select("balance").eq("school_id", sid).in("status", ["sent","partial","overdue"]),
        supabase.from("school_events").select("id,title,start_at,type").eq("school_id", sid).gte("start_at", new Date().toISOString()).order("start_at").limit(5),
        supabase.from("audit_logs").select("id,action,entity_type,created_at").eq("school_id", sid).order("created_at", { ascending: false }).limit(6),
        supabase.from("notifications").select("id,title,body,created_at").eq("school_id", sid).order("created_at", { ascending: false }).limit(4),
      ]);

      const present = (attToday.data ?? []).filter((a) => a.status === "present" || a.status === "late").length;
      const totalAtt = attToday.data?.length ?? 0;
      const collected = (payMonth.data ?? []).reduce((s, p) => s + Number(p.amount), 0);
      const outstanding = (invOpen.data ?? []).reduce((s, i) => s + Number(i.balance), 0);

      setStats({
        learners: learners.count ?? 0,
        teachers: teachers.count ?? 0,
        parents: parents.count ?? 0,
        classes: classes.count ?? 0,
        attendanceToday: { present, total: totalAtt },
        feesCollectedMtd: collected,
        outstanding,
        currency: active.school.currency || "UGX",
      });
      setEvents(ev.data ?? []);
      setActivity(audit.data ?? []);
      setNotifs(n.data ?? []);
    })();
  }, [active]);

  if (loading || !active) {
    return <AdminShell><div className="grid h-64 place-items-center text-muted-foreground">Loading…</div></AdminShell>;
  }

  return (
    <AdminShell
      title={active.school.name}
      subtitle="A live view of your school today"
      action={<Link to="/admin/learners" className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"><Plus className="h-4 w-4" /> Admit learner</Link>}
    >
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Users} k={stats?.learners ?? "—"} v="Active learners" tint="primary" hint="Status: active" />
        <Stat icon={UserCog} k={stats?.teachers ?? "—"} v="Teachers" tint="sky" />
        <Stat icon={Users} k={stats?.parents ?? "—"} v="Parents" tint="gold" />
        <Stat icon={GraduationCap} k={stats?.classes ?? "—"} v="Streams" tint="primary" />
      </section>

      <section className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          icon={ClipboardCheck}
          k={stats ? `${stats.attendanceToday.present}/${stats.attendanceToday.total || "—"}` : "—"}
          v="Attendance today"
          tint="sky"
          hint={stats?.attendanceToday.total ? `${Math.round((stats.attendanceToday.present / stats.attendanceToday.total) * 100)}% present` : "No records yet"}
        />
        <Stat icon={Wallet} k={fmt(stats?.feesCollectedMtd, stats?.currency)} v="Fees collected (month)" tint="primary" />
        <Stat icon={Wallet} k={fmt(stats?.outstanding, stats?.currency)} v="Outstanding balances" tint="gold" />
        <Stat icon={TrendingUp} k={stats ? `${stats.classes}` : "—"} v="Active classes" tint="primary" />
      </section>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <section className="rounded-2xl border border-border bg-card p-5 shadow-soft">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-lg font-bold">Upcoming events</h2>
            <Link to="/admin/calendar" className="text-sm font-semibold text-primary hover:underline">Open calendar</Link>
          </div>
          {events.length === 0 ? (
            <Empty body="No upcoming events yet. Use the calendar to schedule term openings, exams or staff meetings." />
          ) : (
            <ul className="space-y-3">
              {events.map((e) => (
                <li key={e.id} className="flex items-start gap-3">
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary-soft text-primary">
                    <CalIcon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{e.title}</div>
                    <div className="text-xs capitalize text-muted-foreground">{e.type} · {new Date(e.start_at).toLocaleString()}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-2xl border border-border bg-card p-5 shadow-soft">
          <h2 className="font-display text-lg font-bold">Notifications</h2>
          {notifs.length === 0 ? (
            <Empty body="You're all caught up. New alerts will appear here." />
          ) : (
            <ul className="mt-3 space-y-3 text-sm">
              {notifs.map((n) => (
                <li key={n.id} className="flex items-start gap-3">
                  <Bell className="mt-0.5 h-4 w-4 text-primary" />
                  <div className="min-w-0">
                    <div className="truncate font-medium">{n.title}</div>
                    {n.body && <div className="line-clamp-2 text-xs text-muted-foreground">{n.body}</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="mt-6 rounded-2xl border border-border bg-card p-5 shadow-soft">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold">Recent activity</h2>
          <Link to="/admin/audit" className="text-sm font-semibold text-primary hover:underline">View audit log</Link>
        </div>
        {activity.length === 0 ? (
          <Empty body="Sensitive admin actions will appear here as you work." />
        ) : (
          <ul className="divide-y divide-border/60">
            {activity.map((a) => (
              <li key={a.id} className="flex items-center justify-between py-2.5 text-sm">
                <span><b>{a.action}</b> {a.entity_type ? `· ${a.entity_type}` : ""}</span>
                <span className="text-xs text-muted-foreground">{relTime(a.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </AdminShell>
  );
}

function Stat({ icon: Icon, k, v, tint, hint }: { icon: typeof Users; k: React.ReactNode; v: string; tint: "primary"|"gold"|"sky"; hint?: string }) {
  const tintClass = tint === "primary" ? "bg-primary-soft text-primary" : tint === "gold" ? "bg-gold/20 text-gold-foreground" : "bg-sky/15 text-sky";
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
      <div className={`grid h-10 w-10 place-items-center rounded-xl ${tintClass}`}><Icon className="h-5 w-5" /></div>
      <div className="mt-3 font-display text-2xl font-bold">{k}</div>
      <div className="text-xs text-muted-foreground">{v}</div>
      {hint && <div className="mt-1 text-[11px] font-semibold text-primary">{hint}</div>}
    </div>
  );
}

function Empty({ body }: { body: string }) {
  return <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{body}</div>;
}

function fmt(n: number | undefined, ccy = "UGX") {
  if (n === undefined) return "—";
  return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy, maximumFractionDigits: 0 }).format(n);
}

function relTime(iso: string) {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}
